import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, ViewChild } from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RouterLink } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { mfaMailIcon, mfaPhoneIcon, mfaTextIcon } from '@ld.consumer-portal/data-uri';
import {
  MfaCardComponent,
  MfaOption,
  MfaVerificationRequest,
  VerificationCodeCardComponent,
  VerificationCodeSubmit,
} from '@ld.consumer-portal/mfa';
import { KeyboardService, PlatformService } from '@ld.consumer-portal/utils';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

export type PaymentMfaStep = 'select-method' | 'enter-code';

export type PaymentMfaContext = 'add-bank-account' | 'draw-submit';

export interface PaymentMfaSendCodeRequest {
  contactId: string;
  type: 'email' | 'sms' | 'call';
}

export interface PaymentMfaVerifyCodeRequest {
  code: string;
  rememberDevice: boolean;
}

@Component({
  selector: 'ld-payment-mfa-modal',
  imports: [
    UpperCasePipe,
    BaseModalComponent,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatFormFieldModule,
    RouterLink,
    MfaCardComponent,
    VerificationCodeCardComponent,
    TranslocoDirective,
  ],
  templateUrl: './payment-mfa-modal.component.html',
  styleUrl: './payment-mfa-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentMfaModalComponent {
  @ViewChild('verificationCodeCard') verificationCodeCard?: VerificationCodeCardComponent;
  @ViewChild('mfaCard') mfaCard?: MfaCardComponent;

  private readonly transloco = inject(TranslocoService);
  private readonly platformService = inject(PlatformService);
  private readonly keyboardService = inject(KeyboardService);

  protected readonly isBottomSheet = this.platformService.isNativeMobile;
  protected readonly keyboardHeight = this.keyboardService.keyboardOverlayHeight;

  protected readonly mailIcon = mfaMailIcon;
  protected readonly textIcon = mfaTextIcon;
  protected readonly phoneIcon = mfaPhoneIcon;

  protected readonly selectedBsEmail = signal<MfaOption | null>(null);
  protected readonly selectedBsPhone = signal<MfaOption | null>(null);
  protected readonly selectedBsCallPhone = signal<MfaOption | null>(null);

  protected readonly initialBsEmail = computed(() => this.emailOptions()[0] ?? null);
  protected readonly initialBsPhone = computed(() => this.textOptions()[0] ?? null);
  protected readonly initialBsCallPhone = computed(() => this.callOptions()[0] ?? null);

  protected readonly faqLink = computed(() => {
    const id = this.accountId();
    return id ? `/help/${id}/faqs` : '/dashboard';
  });

  private readonly resetMfaCardLoadingOnError = effect(() => {
    const error = this.errorMessage();
    if (error) {
      this.mfaCard?.resetLoading();
    }
  });

  private readonly clearBsSelectionOnLoadingDone = effect(() => {
    if (!this.isLoading()) {
      this.selectedBsEmail.set(null);
      this.selectedBsPhone.set(null);
      this.selectedBsCallPhone.set(null);
    }
  });

  isOpen = input<boolean>(false);

  mfaContext = input<PaymentMfaContext>('add-bank-account');

  accountId = input<string>('');

  emailOptions = input<MfaOption[]>([]);

  textOptions = input<MfaOption[]>([]);

  callOptions = input<MfaOption[]>([]);

  protected readonly isEmailOnly = computed(() => {
    return this.emailOptions().length > 0 && this.textOptions().length === 0 && this.callOptions().length === 0;
  });

  isLoading = input<boolean>(false);

  errorMessage = input<string | null>(null);

  maskedContact = input<string>('');

  sendCode = output<PaymentMfaSendCodeRequest>();

  verifyCode = output<PaymentMfaVerifyCodeRequest>();

  resendCode = output<void>();

  cancel = output<void>();

  protected readonly currentStep = signal<PaymentMfaStep>('select-method');

  protected readonly selectedVerificationType = signal<'email' | 'text' | 'call'>('email');

  protected readonly isVisible = signal(true);

  protected readonly contextDescription = computed(() => {
    return this.mfaContext() === 'draw-submit'
      ? this.transloco.translate('mfaDrawSubmitDescription')
      : this.transloco.translate('mfaPaymentDescription');
  });

  protected readonly contextEmailOnlyDescription = computed(() => {
    if (!this.isEmailOnly()) return null;
    return this.mfaContext() === 'draw-submit'
      ? this.transloco.translate('mfaDrawSubmitEmailOnlyDescription')
      : this.transloco.translate('mfaPaymentEmailOnlyDescription');
  });

  protected onEmailVerification(request: MfaVerificationRequest): void {
    this.selectedVerificationType.set('email');
    this.sendCode.emit({
      contactId: request.value,
      type: 'email',
    });
  }

  protected onTextVerification(request: MfaVerificationRequest): void {
    this.selectedVerificationType.set('text');
    this.sendCode.emit({
      contactId: request.value,
      type: 'sms',
    });
  }

  protected onCallVerification(request: MfaVerificationRequest): void {
    this.selectedVerificationType.set('call');
    this.sendCode.emit({
      contactId: request.value,
      type: 'call',
    });
  }

  protected onMfaCancel(): void {
    this.cancel.emit();
  }

  protected onVerify(event: VerificationCodeSubmit): void {
    this.verifyCode.emit({
      code: event.code,
      rememberDevice: event.rememberDevice,
    });
  }

  protected onResend(): void {
    this.resendCode.emit();
  }

  protected onVerificationCancel(): void {
    this.currentStep.set('select-method');
  }

  showCodeEntry(): void {
    this.currentStep.set('enter-code');
  }

  reset(): void {
    this.currentStep.set('select-method');
    this.selectedVerificationType.set('email');
    this.isVisible.set(true);
    this.selectedBsEmail.set(null);
    this.selectedBsPhone.set(null);
    this.selectedBsCallPhone.set(null);
  }

  setVerificationError(message: string): void {
    this.verificationCodeCard?.setError(message);
  }

  setResendSuccess(message: string): void {
    this.verificationCodeCard?.setSuccess(message);
  }

  protected getVerificationTypeDisplay(): 'email' | 'text' | 'call' {
    return this.selectedVerificationType();
  }

  protected onBsSelectEmail(): void {
    const option = this.selectedBsEmail() ?? this.initialBsEmail();
    if (option) {
      this.onEmailVerification({ method: 'email', value: option.value });
    }
  }

  protected onBsSelectText(): void {
    const option = this.selectedBsPhone() ?? this.initialBsPhone();
    if (option) {
      this.onTextVerification({ method: 'text', value: option.value });
    }
  }

  protected onBsSelectCall(): void {
    const option = this.selectedBsCallPhone() ?? this.initialBsCallPhone();
    if (option) {
      this.onCallVerification({ method: 'call', value: option.value });
    }
  }
}
